/*
 * HotOverlayHandler
 * -----------------
 * Client-side helper to process real-time hot price overlay messages sent by
 * HotAcceleratorEngine (server). Instead of writing to the document via user
 * actions – which would trigger heavy formula recomputations – we intercept the
 * lightweight `hotOverlay` websocket event and update the in-memory DocData
 * model directly. This immediately refreshes visible cells without touching
 * the backend.
 *
 * The handler is gated by the `HOT_FORMULAS_ENABLED` feature flag; when the flag
 * is off, messages are ignored.
 */

import {DocData} from 'app/client/models/DocData';
import {DisposableWithEvents} from 'app/common/DisposableWithEvents';
import {get as getBrowserGlobals} from 'app/client/lib/browserGlobals';

const G = getBrowserGlobals('document', 'window');

/**
 * Hot Overlay Handler - Provides real-time data overlays over formula cells
 *
 * This system displays live market data directly over Grist cells without waiting
 * for Python formula execution, providing instant visual feedback.
 */
export class HotOverlayHandler extends DisposableWithEvents {
  private _overlays = new Map<string, HTMLElement>();

  constructor(docData: DocData) {
    super();
    this._setupOverlayListener();
  }

  /**
   * Setup listener for hotOverlay messages from Grist's comm system
   */
  private _setupOverlayListener(): void {
    // Listen for hotOverlay messages from the server via Grist's comm system
    const app = (G.window as any).gristApp;
    if (app && app.comm) {
      this.listenTo(app.comm, 'hotOverlay', (data: any) => {
        this._handleOverlayMessage(data);
      });
      console.log('🔥 HotOverlayHandler: Listening for hotOverlay messages via Grist comm');
    } else {
      console.warn('⚠️ HotOverlayHandler: Grist comm not available, overlays disabled');
    }
  }

  /**
   * Handle overlay message from Hot Accelerator Engine
   */
  private _handleOverlayMessage(data: any): void {
    console.log('🔥 HotOverlayHandler: Received overlay message:', data);

    if (data.updates && Array.isArray(data.updates)) {
      data.updates.forEach((update: any) => {
        const cellId = `${update.tableId}-${update.colId}-${update.rowId}`;
        
        if (update.type === 'price_update' || update.type === 'ltp_update') {
          this._updateCellOverlay(cellId, update.value, 'price');
        } else if (update.type === 'volume_update') {
          this._updateCellOverlay(cellId, update.value, 'volume');
        } else if (update.type === 'status_update') {
          // Real-time order status overlay
          this._updateCellOverlay(cellId, update.value, 'status');
        }
      });
    }
  }

  /**
   * Update cell overlay with live data
   */
  private _updateCellOverlay(cellId: string, value: string | number, type: string): void {
    const element = document.querySelector(`[data-cell-id="${cellId}"]`) as HTMLElement;
    if (!element) return;

    // Remove existing overlay
    const existingOverlay = element.querySelector('.hot-price-overlay, .hot-volume-overlay, .hot-status-overlay') as HTMLElement;
    if (existingOverlay) {
      existingOverlay.remove();
    }

    // Create new overlay
    const overlay = document.createElement('div');
    overlay.textContent = String(value);
    if (type === 'status') {
      overlay.className = 'hot-status-overlay';
      // Add status-specific styling
      const statusText = String(value).toUpperCase();
      if (statusText.includes('COMPLETE') || statusText.includes('FILLED')) {
        overlay.style.backgroundColor = '#4CAF50'; // Green for filled
      } else if (statusText.includes('PENDING')) {
        overlay.style.backgroundColor = '#FF9800'; // Orange for pending
      } else if (statusText.includes('REJECTED') || statusText.includes('CANCELLED')) {
        overlay.style.backgroundColor = '#F44336'; // Red for rejected/cancelled
      } else {
        overlay.style.backgroundColor = '#2196F3'; // Blue for other statuses
      }
    } else {
      overlay.className = `hot-${type}-overlay`;
    }

    // Apply base styles
    overlay.style.cssText += `
      position: absolute;
      top: 0;
      right: 0;
      background: rgba(33, 150, 243, 0.9);
      color: white;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 11px;
      font-weight: bold;
      z-index: 1000;
      pointer-events: none;
      white-space: nowrap;
    `;

    element.style.position = 'relative';
    element.appendChild(overlay);

    console.log(`🔥 Updated ${type} overlay for cell ${cellId}: ${value}`);
  }

  /**
   * Dispose and cleanup
   */
  public dispose(): void {
    super.dispose();

    this._overlays.forEach(overlay => {
      overlay.remove();
    });
    this._overlays.clear();
  }
}